import { getLocale } from "./locale.js";
import { MAX_CONCEPT_LENGTH } from "./models.js";
import { digitsOf } from "./names.js";
/**
 * The rules about what a person may type, in one place because BOTH SIDES
 * enforce them.
 *
 * A client validates so somebody finds out before a round trip and beside the
 * field they got wrong. The server validates because the client is a JavaScript
 * file anybody can edit. Neither is redundant, and the pair only stays honest
 * if the rule itself has one home — the alternative is a number written by hand
 * in two validators, which is how `LOCALE.phoneDigits` came to claim the server
 * refused anything but eight digits while the server only refused fewer.
 */
/**
 * The most money any single field may carry: B/. 999,999.99.
 *
 * NOT AN OPINION ABOUT PRICES, a column width. `amount_cents` and
 * `amount_due_cents` are `integer` — Postgres int4, which stops at
 * 2,147,483,647 — so an amount above that is not a large number, it is a 500:
 * the statement fails inside the driver and the owner is told the server broke.
 * Zod's `z.int()` allows anything up to 2^53, so without a ceiling here the gap
 * between the two is reachable by typing.
 *
 * A million balboas is far past anything this product bills and far short of
 * the column, which is exactly where a guard belongs.
 */
export const MAX_AMOUNT_CENTS = 99_999_999;
/**
 * The longest period a report may cover. Enforced by the API
 * (`ledger/routes.ts`) and read by the web client so it can say so BEFORE
 * asking — one number, so a client cannot offer a range the server refuses.
 */
export const MAX_REPORT_SPAN_DAYS = 366;
/**
 * The longest period the PDF will render.
 *
 * Tighter than the report's own year, and for a different reason: the CSV and
 * the JSON are a query, and the PDF is a document a Lambda has to lay out page
 * by page. A year of a busy business is hundreds of rows, seconds of CPU and a
 * response big enough to be worth avoiding — and nobody files a year as one
 * PDF anyway.
 *
 * Somebody who wants a quarter wants it in a spreadsheet, which is what the
 * CSV is for. The screen says so rather than letting the button fail.
 */
export const MAX_PDF_SPAN_DAYS = 31;
/**
 * `cash_entry.counterparty` — "familia Rodríguez", "ENSA". Same kind of guard
 * as `MAX_AMOUNT_CENTS` and not a judgement about what a counterparty's name
 * should look like: `payerName`/`memberName` already bound at 80 in
 * `customerSchema`, and a counterparty is the same kind of free-text name, so
 * it gets the same ceiling rather than a number invented for this one field.
 */
export const MAX_COUNTERPARTY_LENGTH = 80;
/**
 * `order.note` and `wash.note` — the free-text line under one of those two
 * DOCUMENTS. Same reasoning as `MAX_AMOUNT_CENTS`: a column width, not an
 * opinion, and the web's own `<Input maxLength>` for this field already used
 * 280 by hand before this constant existed to name it.
 *
 * NEITHER A MOVEMENT NOR A DEBT HAS ONE (0031): in those
 * two `concept` and `note` were two boxes for the same thing, and the one the
 * list reads as the row's title is the one that stayed -- see
 * `MAX_CONCEPT_LENGTH`, which inherited this width for exactly that reason.
 */
export const MAX_NOTE_LENGTH = 280;
/**
 * The longest concept 0031's merge could ever have produced — and therefore
 * the longest one a SAVE has to accept, which is not the same number as the
 * longest one somebody may type.
 *
 * TWO NUMBERS, TWO QUESTIONS. `MAX_CONCEPT_LENGTH` (280) is what a person may
 * TYPE: it is the `maxLength` of every concept field in both clients, and the
 * ceiling every CREATE door enforces, so nothing new is ever written longer
 * than that. This one is what HISTORY MAY HOLD: a row whose «Concepto» and
 * «Nota» were both full got the two joined with « · » when the migration made
 * them one, and that line is longer than either field alone ever was.
 *
 * WHY IT HAS TO BE ACCEPTED AND NOT CUT. A correction form opens with the
 * value already in the row. If the validator refused it, the owner would have
 * to shorten a sentence he wrote on purpose before he could fix the AMOUNT of
 * that movement — the form refusing the very value it was opened with. So the
 * rule is: the validator accepts anything the merge could produce, the input
 * caps what is typed from here on, and a long line shortens at leisure.
 *
 * AN UPPER BOUND, deliberately. The real ceiling was the OLD concept's 120
 * plus the separator plus a note's 280; 120 is gone from the code and a
 * number no constant explains is a number nobody can check, so this is stated
 * in the two widths that still exist. Being generous costs nothing: the
 * column is `text`, and no door lets anybody type their way up here.
 */
export const MAX_MERGED_CONCEPT_LENGTH = MAX_CONCEPT_LENGTH + " · ".length + MAX_NOTE_LENGTH;
/**
 * A customer's phone as it is stored, or null when it is not one.
 *
 * "" is a valid answer and means the customer gave no number — the reminder is
 * simply unavailable for them, which Clientes already says out loud. Anything
 * else must be exactly the digits a Panamanian number has.
 *
 * WHY EXACTLY, AND NOT AT LEAST. The import has always demanded exactly eight
 * and `createCustomer` only refused fewer, so the same list produced different
 * results depending on which screen it came through — and the longer numbers
 * that got in reach `normalizePhoneForWhatsApp`, which reads a 12-digit string
 * as somebody else's country and sends the reminder there.
 *
 * Punctuation is stripped rather than refused: "6000-0000" and "6000 0000" are
 * how people write it, and rejecting them would be the app being pedantic about
 * a detail it can settle itself.
 *
 * EMPTY IS TESTED BEFORE THE DIGITS ARE, and not after. Reading "no digits in
 * it" as "no number given" would take a field somebody typed something into —
 * "no tiene", a name, a note to themselves — and store it as blank without a
 * word. If they typed, it has to be a number.
 */
export function normalizePhone(raw, locale = getLocale()) {
    if (raw.trim() === "")
        return "";
    const digits = digitsOf(raw);
    return digits.length === locale.phoneDigits ? digits : null;
}
/**
 * Whether a date string names a day that exists.
 *
 * The regex every schema already carries checks the SHAPE — four digits, a
 * dash, two, a dash, two — and "2026-02-31" satisfies it. So does "2026-13-01".
 * The database's check constraints are the same regex, so nothing between the
 * form and the disk disagrees with either of them, and what lands is a row no
 * calendar can place: it sorts, it renders, and it is not a day.
 */
export function isCalendarDate(date) {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date))
        return false;
    const year = Number(date.slice(0, 4));
    const month = Number(date.slice(5, 7));
    const day = Number(date.slice(8, 10));
    // Round-tripped through the calendar: Date normalises 31 February into 3
    // March, so a value that comes back changed was never a date.
    const parsed = new Date(Date.UTC(year, month - 1, day));
    return (parsed.getUTCFullYear() === year &&
        parsed.getUTCMonth() === month - 1 &&
        parsed.getUTCDate() === day);
}
/**
 * Whether a payment may be dated this day: a real date, and not in the future.
 *
 * A payment dated tomorrow is the detail that makes somebody stop trusting
 * every other number on the screen — it appears at the top of "Últimos pagos"
 * ahead of money that actually arrived, and `formatRelativeDay` counts the days
 * backwards. Both clients put `max={today}` on the field; only one of them is a
 * file a stranger cannot edit.
 */
export function isRecordablePaymentDate(paidAt, today) {
    return isCalendarDate(paidAt) && paidAt <= today;
}
